/** Cliente HTTP da API. Envia o cabeçalho exigido pela proteção de origem e padroniza os erros. */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const isForm = body instanceof FormData;
  let res: Response;
  try {
    res = await fetch(`/api${url}`, {
      method,
      credentials: "same-origin",
      headers: {
        "X-Requested-With": "book2class",
        ...(body !== undefined && !isForm ? { "Content-Type": "application/json" } : {}),
      },
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network", "Sem conexão com o servidor. Verifique sua internet e tente novamente.");
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = data?.error ?? {};
    throw new ApiError(res.status, err.code ?? "error", err.message ?? "Algo deu errado.", err.fields ?? {});
  }
  // Medalhas conquistadas em qualquer ação são anunciadas de forma global (ver BadgeToaster).
  if (method !== "GET" && Array.isArray(data?.badges) && data.badges.length)
    window.dispatchEvent(new CustomEvent("b2c:badges", { detail: data.badges }));
  return data as T;
}

export const api = {
  get: <T>(url: string) => request<T>("GET", url),
  post: <T>(url: string, body?: unknown) => request<T>("POST", url, body ?? {}),
  patch: <T>(url: string, body?: unknown) => request<T>("PATCH", url, body ?? {}),
  put: <T>(url: string, body?: unknown) => request<T>("PUT", url, body ?? {}),
  del: <T>(url: string, body?: unknown) => request<T>("DELETE", url, body),
};

/** Envio com acompanhamento de progresso (fetch ainda não informa o progresso de upload). */
export function uploadWithProgress<T>(
  url: string,
  form: FormData,
  onProgress: (pct: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api${url}`);
    xhr.setRequestHeader("X-Requested-With", "book2class");
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
    xhr.onerror = () => reject(new ApiError(0, "network", "Falha de conexão durante o envio."));
    xhr.onload = () => {
      let data: { error?: { code?: string; message?: string; fields?: Record<string, string> } } | null =
        null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        /* resposta sem JSON */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
      else {
        const err = data?.error ?? {};
        reject(
          new ApiError(xhr.status, err.code ?? "error", err.message ?? "Falha no envio.", err.fields ?? {}),
        );
      }
    };
    xhr.send(form);
  });
}

export const formatBytes = (n: number) =>
  n < 1024 * 1024
    ? `${Math.max(1, Math.round(n / 1024))} KB`
    : `${(n / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;

export const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
